/**
 * Exactly what the customer agrees to when they consent to having their
 * web-UI session recorded. POSTed verbatim with the consent record.
 *
 * Lives in its own module rather than beside the component for two
 * reasons. The mechanical one is fast refresh, which only works when a
 * component file exports components. The better one is that this string
 * is the *substance* of the consent — it is stored alongside it so that
 * "what were they actually told" is answerable later — and burying it in
 * a component invites someone to reword it as a UI tweak without
 * realising they have changed what people agreed to.
 *
 * Composed rather than constant, because there is now more than one reason to
 * be asking, and the old opening — "We could not identify this device" — is
 * plainly false on a device someone chose to record themselves. What varies
 * is *only* the reason: every promise about what is taken, what is removed,
 * and what the customer gets to decide is shared, so the wordings cannot
 * drift apart on the parts that matter. A new reason adds an opening and
 * inherits the rest.
 *
 * Written to answer the two questions people actually have — what gets
 * taken, and what does not — rather than to be legally exhaustive.
 */

/** Why we are asking. Decides the opening, and nothing else. */
export type CaptureReason =
  /** We could not identify the device and want to learn how to support it. */
  | 'unidentified'
  /** The customer started this themselves, to show us something. */
  | 'userInitiated'
  /** We asked to be shown one specific thing on a device we already support. */
  | 'requested';

const OPENING: Record<CaptureReason, string> = {
  unidentified: [
    'We could not identify this device, so we do not yet know how to manage it.',
    'You can help by showing us its web interface.',
  ].join(' '),
  userInitiated: [
    'You have chosen to record this session so you can show us something about this',
    'device.',
  ].join(' '),
  // Filled in with the specific ask by `disclosureFor`. We support this
  // device; what we are missing is how one particular thing is done on it.
  requested: 'We support this device, but we have not yet worked out {ask}.',
};

/** What we do, in every case. */
const WHAT_HAPPENS = [
  'We will record the pages you visit while you browse, and you can review exactly',
  'what was captured before deciding whether to share it.',
].join(' ');

/** What never leaves the gateway, in every case. */
const WHAT_IS_REMOVED = [
  'Passwords, session cookies, API tokens and SNMP/wireless/RADIUS secrets are removed',
  'on your gateway before anything is shown to you or sent to us. Page layout, form',
  'field names and HTML are kept — that is what lets us add support for this device.',
].join(' ');

/**
 * Only true when recording has to start from the first request. A forced
 * re-login with no warning reads as a bug, so where it happens we say so —
 * and where it does not, we must not. "May", because where the gateway signs
 * in with a stored credential it logs in again itself, and the person types
 * nothing.
 */
const RELOGIN = [
  'Starting will reconnect your session, so you may need to log in to the device',
  'again. Where Witina signs in to it for you with a stored credential, it does that',
  'itself.',
].join(' ');

/**
 * Why the re-login is worth it. Only true when authoring a profile from
 * nothing — on a device we already support, the login page is the least
 * interesting thing the customer could show us.
 */
const LOGIN_IS_THE_POINT = [
  'That first login is the most useful page for us, which is why recording has to',
  'begin before it.',
].join(' ');

/**
 * Deliberately "helps us" rather than "will let us". Someone who walks us
 * through creating a VLAN will reasonably expect VLAN management to appear,
 * and promising that on the strength of one recording is a promise we cannot
 * keep.
 */
const WHAT_IT_BUYS =
  'Walking us through it helps us add support for that operation on this hardware.';

/**
 * `ask` completes the `requested` opening — an ask with no subject is not a
 * question, so it falls back to something honest rather than empty.
 *
 * The paragraph a reason gets is chosen, not patched: the two that record from
 * the first request warn about the re-login, and `requested` does not, because
 * for a device we already support recording starts on the session the customer
 * already has open.
 */
export function disclosureFor(reason: CaptureReason, ask?: string): string {
  const opening = OPENING[reason].replace('{ask}', ask ?? 'how to do this');
  const parts = [opening, '', WHAT_HAPPENS, '', WHAT_IS_REMOVED, ''];

  if (reason === 'requested') {
    parts.push(WHAT_IT_BUYS);
  } else {
    parts.push(RELOGIN);
    if (reason === 'unidentified') parts.push(LOGIN_IS_THE_POINT);
  }
  return parts.join(' ').trim();
}

/** The wording for a device we could not identify. */
export const DISCLOSURE_TEXT = disclosureFor('unidentified');
